import { AnimatePresence, motion } from 'framer-motion'
import { AlertTriangle, Bot, MessageCircleOff, User, UserRound } from 'lucide-react'
import type { ConversationSummary } from '../api'
import { timeAgo } from '../utils/time'
import { Avatar } from './Avatar'

interface Props {
  conversations: ConversationSummary[] | undefined
  isLoading: boolean
  isError: boolean
  selectedPhone: string | null
  onSelect: (phone: string) => void
}

export function ConversationList({ conversations, isLoading, isError, selectedPhone, onSelect }: Props) {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white/70 shadow-sm dark:border-slate-800 dark:bg-slate-900/60 dark:shadow-black/20">
      <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Conversaciones activas
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="space-y-3 p-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800/60" />
            ))}
          </div>
        )}

        {!isLoading && isError && (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-16 text-center text-rose-500">
            <AlertTriangle className="h-8 w-8" />
            <p className="text-sm">No se pudo cargar la lista de conversaciones.</p>
            <p className="text-xs text-slate-400 dark:text-slate-600">
              Verifica que el backend esté corriendo y vuelve a intentar.
            </p>
          </div>
        )}

        {!isLoading && !isError && conversations?.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-16 text-center text-slate-500">
            <MessageCircleOff className="h-8 w-8" />
            <p className="text-sm">No hay conversaciones activas todavía.</p>
            <p className="text-xs text-slate-400 dark:text-slate-600">
              Envía un mensaje de prueba para verla aquí.
            </p>
          </div>
        )}

        <ul className="divide-y divide-slate-200 dark:divide-slate-800/80">
          <AnimatePresence initial={false}>
            {conversations?.map((conv) => {
              const isSelected = conv.phone === selectedPhone
              return (
                <motion.li
                  key={conv.phone}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="relative"
                >
                  {isSelected && <span className="absolute inset-y-0 left-0 w-0.5 bg-indigo-400" />}
                  <motion.button
                    type="button"
                    onClick={() => onSelect(conv.phone)}
                    whileTap={{ scale: 0.98 }}
                    className={`flex w-full items-start gap-3 px-5 py-3.5 text-left transition-colors ${
                      isSelected ? 'bg-indigo-500/10' : 'hover:bg-slate-100 dark:hover:bg-slate-800/40'
                    }`}
                  >
                    <Avatar name={conv.client_name} phone={conv.phone} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                          {conv.client_name || conv.phone}
                        </span>
                        <span className="shrink-0 text-xs text-slate-500">
                          {timeAgo(conv.last_timestamp)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500">
                        <span className="truncate">{conv.phone}</span>
                        <span>·</span>
                        <span>{conv.message_count} msj.</span>
                        <AnimatePresence>
                          {conv.handoff_requested && (
                            <motion.span
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              className="shrink-0 rounded-full bg-rose-500/10 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 ring-1 ring-rose-500/30 dark:text-rose-300"
                            >
                              Pide agente
                            </motion.span>
                          )}
                          {conv.paused && (
                            <motion.span
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              className="shrink-0 rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-500/30 dark:text-amber-300"
                            >
                              Pausado
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </div>
                      <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
                        {conv.last_role === 'assistant' ? (
                          <Bot className="h-3.5 w-3.5 shrink-0 text-indigo-500 dark:text-indigo-400" />
                        ) : conv.last_role === 'agent' ? (
                          <UserRound className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                        ) : (
                          <User className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                        )}
                        <span className="truncate">{conv.last_message}</span>
                      </div>
                    </div>
                  </motion.button>
                </motion.li>
              )
            })}
          </AnimatePresence>
        </ul>
      </div>
    </div>
  )
}
